// The strolling root in a page header (Home, Orders), in place of the "Do with root" button (owner,
// 2026-10-24). The strip is one button the size of a control row, so the header keeps its height; root
// (64 px) stands in it, overflowing up a little, and strolls. Clicking anywhere on the strip opens
// root's panel. three.js and the GLB load only once the page is idle; the 2D bot stands at the right
// end until root is on screen (and stays if WebGL is unavailable). Paused while off screen.
import { t } from "@ady/i18n";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { cx } from "@ady/ui";
import { mascotStateFor, useAssistantSession } from "./assistantSession";
import { useOnScreen } from "./canvasSupport";

import { RootHead } from "./RootHead";
const RootStrollScene = lazy(() => import("./RootStrollScene"));

function whenIdle(fn: () => void): () => void {
  if (typeof requestIdleCallback === "function") {
    const id = requestIdleCallback(fn, { timeout: 2500 });
    return () => cancelIdleCallback(id);
  }
  const id = window.setTimeout(fn, 1200);
  return () => window.clearTimeout(id);
}

export function StrollingRoot({
  onOpen,
  sit = false,
  className,
}: {
  onOpen: () => void;
  /** Sit in place (a narrow spot) instead of strolling. */
  sit?: boolean;
  className?: string;
}) {
  const session = useAssistantSession();
  const [load, setLoad] = useState(false);
  const [ready, setReady] = useState(false);
  const [hover, setHover] = useState(false);
  const box = useRef<HTMLButtonElement>(null);
  const visible = useOnScreen(box);
  useEffect(() => whenIdle(() => setLoad(true)), []);

  return (
    <button
      ref={box}
      type="button"
      aria-label={t("shell.openAssistant")}
      onClick={onOpen}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className={cx(
        "relative flex min-h-control flex-none items-center justify-end rounded-xs",
        sit ? "w-9" : "w-36",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cx("flex size-8 items-center justify-center", ready && "invisible")}
      >
        <RootHead size={26} />
      </span>
      {load ? (
        <span
          aria-hidden="true"
          className={cx(
            "pointer-events-none absolute right-0",
            // Seated root fits a tight gap: a smaller canvas overflowing a 36 px spot to the left.
            sit ? "-bottom-2 h-12 w-12" : "-bottom-3 h-16 w-36",
            !ready && "opacity-0",
          )}
        >
          <Suspense fallback={null}>
            <RootStrollScene
              sit={sit}
              busy={mascotStateFor(session.status)}
              hover={hover}
              visible={visible}
              onReady={() => setReady(true)}
            />
          </Suspense>
        </span>
      ) : null}
    </button>
  );
}
